import { useState, useEffect } from 'react';

export function objectToUrlParams(obj: Record<string, string[] | string>) {
    const params = new URLSearchParams();

    // Compose query params for filters
    Object.keys(obj).forEach((k) => {
        const value = obj[k];

        if (Array.isArray(value)) {
            // Ensure all array values are added to query strings
            value.forEach((v) => {
                params.append(k, v);
            });
        } else {
            params.set(k, value);
        }
    });

    return params;
}

/**
 * Reflects object state to URL query strings.
 *
 * @param {object} [initialState] - Intial object to set as url query parameters.
 * If null or other falsy value, will not make any changes to the url; use this
 * functionality to skip url updates until some internal app logic has completed.
 * @returns {array} - A tuple:
 * - First item is an object representing the current url query param state
 * - Second item is a function that is called with a new state object, to update
 *   the url quary params to that state.
 */
export const useQueryParams = (initialState: Record<string, string[] | string> | undefined) => {
    const [query, setQueryParams] = useState(initialState);

    useEffect(() => {
        if (!query) return;

        const params = objectToUrlParams(query);

        // Use replaceState instead of pushState so we don't generate a new
        // browser history for each filter change.
        window.history.replaceState(
            null,
            '',
            params && params.entries.length
                ? `${window.location.href}?${params.toString()}`
                : window.location.href
        );
    }, [query]);

    return [query, setQueryParams];
};
